const FIRST_TOUCH_STORAGE_KEY = 'deeply_lead_first_touch:v1';
const SESSION_TOUCH_STORAGE_KEY = 'deeply_lead_session_touch:v1';
const CONVERSION_ORIGIN_STORAGE_KEY = 'deeply_lead_conversion_origin:v1';
const FIRST_TOUCH_TTL_MS = 90 * 24 * 60 * 60 * 1000;
const MAX_VALUE_LENGTH = 100;

const AI_REFERRERS: ReadonlyArray<{
  hosts: readonly string[];
  source: string;
}> = [
  { hosts: ['chatgpt.com', 'chat.openai.com'], source: 'chatgpt' },
  { hosts: ['perplexity.ai'], source: 'perplexity' },
  { hosts: ['gemini.google.com'], source: 'gemini' },
  { hosts: ['claude.ai'], source: 'claude' },
  { hosts: ['copilot.microsoft.com'], source: 'copilot' },
];

export type LeadAttributionTouch = {
  campaign?: string;
  captured_at: string;
  content?: string;
  landing_page: string;
  medium: string;
  referrer_domain?: string;
  source: string;
  term?: string;
};

export type LeadAttribution = {
  conversion_origin?: string;
  conversion_page: string;
  first_touch: LeadAttributionTouch;
  session_touch: LeadAttributionTouch;
};

type CreateTouchInput = {
  capturedAt?: Date;
  locationHref: string;
  referrer: string;
};

let capturedCurrentDocument = false;

function sanitizeValue(value: unknown, maxLength = MAX_VALUE_LENGTH) {
  if (typeof value !== 'string') return undefined;

  const sanitized = value.replace(/[\u0000-\u001f\u007f]/g, '').trim();
  if (!sanitized || sanitized.includes('@')) return undefined;
  return sanitized.slice(0, maxLength);
}

function normalizePath(value: unknown) {
  const path = sanitizeValue(value, 300);
  return path?.startsWith('/') ? path : undefined;
}

function referrerDomain(referrer: string, currentHostname: string) {
  if (!referrer) return undefined;

  try {
    const hostname = new URL(referrer).hostname.toLowerCase();
    return hostname && hostname !== currentHostname.toLowerCase()
      ? hostname
      : undefined;
  } catch {
    return undefined;
  }
}

function aiSourceForDomain(domain?: string) {
  if (!domain) return undefined;

  return AI_REFERRERS.find(({ hosts }) =>
    hosts.some((host) => domain === host || domain.endsWith(`.${host}`)),
  )?.source;
}

export function createLeadAttributionTouch({
  capturedAt = new Date(),
  locationHref,
  referrer,
}: CreateTouchInput): LeadAttributionTouch {
  const url = new URL(locationHref);
  const externalReferrer = referrerDomain(referrer, url.hostname);
  const aiSource = aiSourceForDomain(externalReferrer);
  const campaignSource = sanitizeValue(url.searchParams.get('utm_source'));
  const campaignMedium = sanitizeValue(url.searchParams.get('utm_medium'));

  return {
    campaign: sanitizeValue(url.searchParams.get('utm_campaign')),
    captured_at: capturedAt.toISOString(),
    content: sanitizeValue(url.searchParams.get('utm_content')),
    landing_page: url.pathname || '/',
    medium:
      campaignMedium ??
      (aiSource ? 'ai_referral' : externalReferrer ? 'referral' : 'none'),
    referrer_domain: externalReferrer,
    source: campaignSource ?? aiSource ?? externalReferrer ?? 'direct',
    term: sanitizeValue(url.searchParams.get('utm_term')),
  };
}

function normalizeTouch(value: unknown): LeadAttributionTouch | undefined {
  if (!value || typeof value !== 'object') return undefined;

  const touch = value as Record<string, unknown>;
  const capturedAt = sanitizeValue(touch.captured_at, 40);
  const landingPage = normalizePath(touch.landing_page);
  const medium = sanitizeValue(touch.medium);
  const source = sanitizeValue(touch.source);
  if (!capturedAt || !landingPage || !medium || !source) return undefined;
  if (Number.isNaN(Date.parse(capturedAt))) return undefined;

  return {
    campaign: sanitizeValue(touch.campaign),
    captured_at: capturedAt,
    content: sanitizeValue(touch.content),
    landing_page: landingPage,
    medium,
    referrer_domain: sanitizeValue(touch.referrer_domain),
    source,
    term: sanitizeValue(touch.term),
  };
}

export function normalizeLeadAttribution(
  value: unknown,
): LeadAttribution | undefined {
  if (!value || typeof value !== 'object') return undefined;

  const attribution = value as Record<string, unknown>;
  const conversionOrigin = normalizePath(attribution.conversion_origin);
  const conversionPage = normalizePath(attribution.conversion_page);
  const firstTouch = normalizeTouch(attribution.first_touch);
  const sessionTouch = normalizeTouch(attribution.session_touch);
  if (!conversionPage || !firstTouch || !sessionTouch) return undefined;

  return {
    conversion_origin: conversionOrigin,
    conversion_page: conversionPage,
    first_touch: firstTouch,
    session_touch: sessionTouch,
  };
}

export function recordLeadAttributionPage(pathname: string) {
  if (typeof window === 'undefined' || pathname.includes('/inquiry')) return;

  const normalizedPath = normalizePath(pathname);
  if (!normalizedPath) return;

  try {
    window.sessionStorage.setItem(
      CONVERSION_ORIGIN_STORAGE_KEY,
      normalizedPath,
    );
  } catch {
    // 저장소가 차단된 환경에서도 페이지 이동은 계속 진행한다
  }
}

function readTouch(storage: Storage, key: string) {
  try {
    const stored = storage.getItem(key);
    return stored ? normalizeTouch(JSON.parse(stored)) : undefined;
  } catch {
    return undefined;
  }
}

function writeTouch(
  storage: Storage,
  key: string,
  touch: LeadAttributionTouch,
) {
  try {
    storage.setItem(key, JSON.stringify(touch));
  } catch {
    // 저장소가 차단된 환경에서도 문의 제출은 계속 진행한다
  }
}

function readStoredPath(storage: Storage, key: string) {
  try {
    return normalizePath(storage.getItem(key));
  } catch {
    return undefined;
  }
}

function isFresh(touch: LeadAttributionTouch, now: number) {
  return now - Date.parse(touch.captured_at) <= FIRST_TOUCH_TTL_MS;
}

export function captureLeadAttribution() {
  if (typeof window === 'undefined' || capturedCurrentDocument) return;
  capturedCurrentDocument = true;

  const touch = createLeadAttributionTouch({
    locationHref: window.location.href,
    referrer: window.document.referrer,
  });
  const firstTouch = readTouch(window.localStorage, FIRST_TOUCH_STORAGE_KEY);
  const sessionTouch = readTouch(
    window.sessionStorage,
    SESSION_TOUCH_STORAGE_KEY,
  );
  const hasCampaign = new URL(window.location.href).searchParams.has(
    'utm_source',
  );

  if (!firstTouch || !isFresh(firstTouch, Date.now())) {
    writeTouch(window.localStorage, FIRST_TOUCH_STORAGE_KEY, touch);
  }
  if (!sessionTouch || hasCampaign || touch.referrer_domain) {
    writeTouch(window.sessionStorage, SESSION_TOUCH_STORAGE_KEY, touch);
  }
}

export function readLeadAttribution(): LeadAttribution | undefined {
  if (typeof window === 'undefined') return undefined;
  captureLeadAttribution();

  const firstTouch = readTouch(window.localStorage, FIRST_TOUCH_STORAGE_KEY);
  const sessionTouch = readTouch(
    window.sessionStorage,
    SESSION_TOUCH_STORAGE_KEY,
  );
  if (!firstTouch || !sessionTouch) return undefined;

  return {
    conversion_origin: readStoredPath(
      window.sessionStorage,
      CONVERSION_ORIGIN_STORAGE_KEY,
    ),
    conversion_page: window.location.pathname || '/',
    first_touch: firstTouch,
    session_touch: sessionTouch,
  };
}
